import React, { useState, useEffect } from 'react';
import { httpRequest } from 'src/services/httpRequest';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControlLabel,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  InputAdornment,
  Fade,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import LabelIcon from '@mui/icons-material/Label';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface Status {
  StatusID: number;
  Status: string;
  Active: boolean;
  RowStatus: number;
  CreateDate: string;
  CreatedBy: number;
  UpdateDate: string | null;
  UpdatedBy: number | null;
  DeleteDate: string | null;
  DeletedBy: number | null;
  TownshipID: number;
}

interface FormErrors {
  status: string;
}

interface TouchedFields {
  status: boolean;
}

const StatusMaster: React.FC = () => {
  const theme = useTheme();

  const [statusData, setStatusData] = useState<Status[]>([]);
  const [newStatus, setNewStatus] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [touched, setTouched] = useState<TouchedFields>({
    status: false
  });
  const [errors, setErrors] = useState<FormErrors>({
    status: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const userRole = userDetails.UserRole || '';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const fetchStatusData = async (pageNumber: number, pageSize: number) => {
    setTableLoading(true);
    try {
      const response = await httpRequest(
        'GET',
        `/bid_portal/status/get_all?Page=${
          pageNumber + 1
        }&Size=${pageSize}&Active=false${
          debouncedSearchTerm ? `&Status=${debouncedSearchTerm}` : ''
        }`
      );

      if (response.status === 'success' && response.data) {
        setStatusData(response.data.data || []);
        setTotalCount(response.data.totalRecords || 0);
      } else {
        toast.error(response.message || 'Failed to fetch status data');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error loading status data');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchStatusData(page, rowsPerPage);
  }, [page, rowsPerPage, debouncedSearchTerm]);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'status':
        return !value.trim() ? 'Status cannot be blank' : '';
      default:
        return '';
    }
  };

  const handleBlur = (field: keyof TouchedFields) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true
    }));

    const error = validateField(field, newStatus);
    setErrors((prev) => ({
      ...prev,
      [field]: error
    }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearForm = () => {
    setNewStatus('');
    setIsActive(true);
    setEditingId(null);
    setTouched({ status: false });
    setErrors({ status: '' });
    setMessage('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const statusError = validateField('status', newStatus);
    setErrors({ status: statusError });
    setTouched({ status: true });

    if (statusError) {
      return;
    }

    setIsLoading(true);
    try {
      let response;

      if (editingId) {
        // Update existing status
        response = await httpRequest(
          'PATCH',
          `/bid_portal/status/update/${editingId}`,
          {
            Status: newStatus,
            Active: isActive
          }
        );
      } else {
        // Create new status
        response = await httpRequest('POST', '/bid_portal/status/insert', {
          Status: newStatus
        });
      }

      if (response.status === 'success') {
        setMessage(
          editingId
            ? 'Status updated successfully'
            : 'Status created successfully'
        );

        clearForm();
        fetchStatusData(page, rowsPerPage);
      } else {
        setErrorMessage(response.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving data:', error);
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (status: Status) => {
    setEditingId(status.StatusID);
    setNewStatus(status.Status);
    setIsActive(status.Active);
    setTouched({ status: false });
    setErrors({ status: '' });
    setMessage('');
  };

  // Format date function - show only date, not time
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: alpha(theme.palette.background.default, 0.4),
        borderRadius: 2,
        minHeight: '100vh'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
        <LabelIcon
          sx={{
            fontSize: 32,
            color: theme.palette.primary.main
          }}
        />
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 'bold'
          }}
        >
          Bid Status Management
        </Typography>
      </Stack>

      {message && (
        <Fade in={!!message}>
          <Alert
            severity="success"
            sx={{
              mb: 3,
              boxShadow: 2,
              borderRadius: 2
            }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        </Fade>
      )}

      {errorMessage && (
        <Fade in={!!errorMessage}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              boxShadow: 2,
              borderRadius: 2
            }}
            onClose={() => setErrorMessage('')}
          >
            {errorMessage}
          </Alert>
        </Fade>
      )}

      {userRole === 'Admin' && (
        <Card
          elevation={2}
          sx={{
            mb: 4,
            borderRadius: 2,
            overflow: 'visible'
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {editingId ? (
                  <EditIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                ) : (
                  <AddCircleOutlineIcon
                    sx={{ mr: 1, color: theme.palette.primary.main }}
                  />
                )}
                <Typography variant="h6">
                  {editingId ? 'Edit Status' : 'Create New Status'}
                </Typography>
                {editingId && (
                  <Chip
                    label={`ID: ${editingId}`}
                    size="small"
                    sx={{
                      ml: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              pb: 1
            }}
          />
          <CardContent sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    label="Status Name"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    onBlur={() => handleBlur('status')}
                    error={touched.status && !!errors.status}
                    helperText={touched.status && errors.status}
                    fullWidth
                    InputProps={{
                      sx: { borderRadius: 1 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <LabelIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                {editingId && (
                  <Grid item xs={12} md={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: alpha(
                          isActive
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.05
                        ),
                        border: `1px solid ${alpha(
                          isActive
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.2
                        )}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isActive ? (
                          <CheckCircleOutlineIcon
                            sx={{ color: theme.palette.success.main, mr: 1 }}
                          />
                        ) : (
                          <HighlightOffIcon
                            sx={{ color: theme.palette.error.main, mr: 1 }}
                          />
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            color: isActive
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 500
                          }}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            color={isActive ? 'success' : 'error'}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12} md={editingId ? 2 : 6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                  >
                    <Button
                      variant="outlined"
                      onClick={clearForm}
                      startIcon={<ClearIcon />}
                      sx={{
                        borderRadius: 1,
                        px: 2
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={20} />
                        ) : editingId ? (
                          <SaveIcon />
                        ) : (
                          <AddCircleOutlineIcon />
                        )
                      }
                      sx={{
                        borderRadius: 1,
                        px: 3
                      }}
                    >
                      {isLoading
                        ? 'Saving...'
                        : editingId
                        ? 'Update'
                        : 'Create'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table Section */}
      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ViewListIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                <Typography variant="h6">All Statuses</Typography>
                <Chip
                  label={`${totalCount} total`}
                  size="small"
                  sx={{
                    ml: 2,
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    color: theme.palette.primary.main
                  }}
                />
              </Box>

              <TextField
                placeholder="Search statuses..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.default, 0.7)
                  }
                }}
                sx={{ width: { xs: '100%', sm: 250 } }}
              />
            </Box>
          }
          sx={{
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        />

        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper,
                    py: 2
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Created Date
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 18 }} />
                    Updated Date
                  </Box>
                </TableCell>
                {userRole === 'Admin' && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: theme.palette.background.paper
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={30} />
                    <Typography
                      variant="body2"
                      sx={{ mt: 2, color: theme.palette.text.secondary }}
                    >
                      Loading statuses...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : statusData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm
                        ? `No statuses matching "${searchTerm}" found`
                        : 'No statuses found'}
                    </Typography>
                    {searchTerm && (
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<ClearIcon />}
                        onClick={() => setSearchTerm('')}
                        sx={{ mt: 1 }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                statusData.map((status) => (
                  <TableRow
                    key={status.StatusID}
                    sx={{
                      '&:nth-of-type(odd)': {
                        bgcolor: alpha(theme.palette.background.default, 0.3)
                      },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.light, 0.05)
                      }
                    }}
                  >
                    <TableCell sx={{ py: 2, fontWeight: 'medium' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LabelIcon
                          sx={{
                            mr: 1,
                            color: theme.palette.primary.main,
                            fontSize: 18
                          }}
                        />
                        {status.Status}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={status.Active ? 'Active' : 'Inactive'}
                        icon={
                          status.Active ? (
                            <CheckCircleOutlineIcon />
                          ) : (
                            <HighlightOffIcon />
                          )
                        }
                        sx={{
                          backgroundColor: status.Active
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                          color: status.Active
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          fontWeight: 500,
                          borderRadius: 1,
                          '& .MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={status.CreateDate} arrow>
                        <Typography variant="body2">
                          {formatDate(status.CreateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={status.UpdateDate || ''} arrow>
                        <Typography variant="body2">
                          {formatDate(status.UpdateDate)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {userRole === 'Admin' && (
                      <TableCell align="center">
                        <Tooltip title="Edit Status" arrow>
                          <Button
                            onClick={() => handleEdit(status)}
                            size="small"
                            variant="text"
                            color="primary"
                            startIcon={<EditIcon />}
                            sx={{
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.04
                                )
                              }
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
              {
                my: 1
              }
          }}
        />
      </Card>

      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            backdropFilter: 'blur(4px)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="subtitle1">
              {editingId ? 'Updating status...' : 'Creating status...'}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default StatusMaster;
